import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { NavigationEnd, Router } from "@angular/router";
import { ShellService } from "@cotecna/shell";
import { Subscription } from "rxjs";
import { User } from "src/app/session/models/user";
import { LogoutService } from "src/app/session/services/logout/logout.service";
import { UserService } from "src/app/session/services/user.service";
import { AppSettings } from "src/app/settings/models/app-settings";
import { AppSettingsService } from "src/app/settings/services/app-settings.service";
import { FeatureFlagsService } from "src/app/shared/services/feature-flags/feature-flags.service";
import { UserRolePermissionsService } from "src/app/shared/services/user-role-permissions/user-role-permissions.service";

import {
  ABOUT_ACTION_NAME,
  BI_REPORTS_ACTION_NAME,
  DASHBOARDS_ACTION_NAME,
  INSPECTIONS_ACTION_NAME,
  LOGOUT_ACTION_NAME,
  MULTIOFFICE_ACTION_NAME,
  REPORT_ISSUE_ACTION_NAME,
  RESOURCES_ACTION_NAME,
  SEARCH_ACTION_NAME,
  SETTINGS_ACTION_NAME,
  TIMESHEET_ACTIVITIES_ACTION_NAME,
} from "../constants/constants";
import { MultiofficeDialogComponent } from "../dialogs/multioffice-dialog/multioffice-dialog.component";
import {
  SearchDialogComponent,
  SearchDialogResult,
} from "../inspections/search-component/search-dialog.component";
import { AppShellService } from "../services/app-shell.service";
import { InspectionListService } from "../services/inspection-list.service";
import { PermissionsService } from "../services/permissions.service";
import { ReportIssueService } from "../services/report-issue.service";

@Component({
  selector: "im-home",
  templateUrl: "./home.component.html",
  styleUrls: ["./home.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class HomeComponent implements OnInit, OnDestroy {
  private appSettings: AppSettings = null;
  private routerSubscription: Subscription;
  private toolbarActionSubscription: Subscription;
  private user: User;

  public constructor(
    public shellService: AppShellService,
    private userRolePermissionsService: UserRolePermissionsService,
    private inspectionListService: InspectionListService,
    private logoutService: LogoutService,
    private dialog: MatDialog,
    private cotecnaShellService: ShellService,
    private router: Router,
    private featureFlagsService: FeatureFlagsService,
    private userService: UserService,
    private permissionsService: PermissionsService,
    private appSettingsService: AppSettingsService,
    private reportIssueService: ReportIssueService,
    private readonly appShellService: AppShellService,
  ) {
    this.routerSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        const selectedOption = event?.urlAfterRedirects?.split("/")[1];
        if (selectedOption) this.setSideMenuStatuses(selectedOption);
      }
    });
  }

  public ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();
    this.toolbarActionSubscription?.unsubscribe();
  }

  public async ngOnInit(): Promise<void> {
    this.toolbarActionSubscription =
      this.cotecnaShellService.toolbarActionClicked.subscribe(
        (action: string) => this.onToolbarActionClicked(action),
      );
    this.appSettings = await this.appSettingsService.get();

    //TODO: Move/split logic inside appShellservice && userAccountService

    this.user = await this.userService.getCurrentUser();
    this.user.selectedAcc = this.user.accountId;

    if (
      this.appSettings.ticketingReportingEnabled &&
      this.permissionsService.isCurrentUserInternal()
    )
      this.shellService.tryAddReportIssueAction();

    if (this.userRolePermissionsService.isInspector()) {
      this.shellService.sideMenuOptions.sidemenuActions =
        this.shellService.sideMenuOptions.sidemenuActions.filter(
          (action) => action.id != MULTIOFFICE_ACTION_NAME,
        );
    }
    if (!this.userRolePermissionsService.canAccessToInspectionsList()) {
      this.shellService.sideMenuOptions.sidemenuActions =
        this.shellService.sideMenuOptions.sidemenuActions.filter(
          (action) => action.id != INSPECTIONS_ACTION_NAME,
        );
    }
    if (!this.userRolePermissionsService.canAccessToResources()) {
      this.shellService.sideMenuOptions.sidemenuActions =
        this.shellService.sideMenuOptions.sidemenuActions.filter(
          (action) => action.id != RESOURCES_ACTION_NAME,
        );
    }
    if (!this.userRolePermissionsService.canAccessToSettings()) {
      this.shellService.sideMenuOptions.sidemenuActions =
        this.shellService.sideMenuOptions.sidemenuActions.filter(
          (action) => action.id != SETTINGS_ACTION_NAME,
        );
    }
    if (!this.userRolePermissionsService.canAccessToDashboards()) {
      this.shellService.sideMenuOptions.sidemenuActions =
        this.shellService.sideMenuOptions.sidemenuActions.filter(
          (action) => action.id != DASHBOARDS_ACTION_NAME,
        );
    }
    if (!this.userRolePermissionsService.canAccessToAbout()) {
      this.shellService.sideMenuOptions.sidemenuActions =
        this.shellService.sideMenuOptions.sidemenuActions.filter(
          (action) => action.id != ABOUT_ACTION_NAME,
        );
    }
    if (
      !this.userRolePermissionsService.canAccessToDashboard(
        this.user.accountName,
      )
    ) {
      this.shellService.sideMenuOptions.sidemenuActions =
        this.shellService.sideMenuOptions.sidemenuActions.filter(
          (action) => action.id != BI_REPORTS_ACTION_NAME,
        );
    }
    if (this.userRolePermissionsService.isInspector()) {
      this.shellService.sideMenuOptions.sidemenuActions =
        this.shellService.sideMenuOptions.sidemenuActions.filter(
          (action) => action.id != MULTIOFFICE_ACTION_NAME,
        );
    }
    if (
      this.userRolePermissionsService.isInspectionsOnlyAccount() ||
      !this.userRolePermissionsService.canAccessToTimesheetActivities()
    ) {
      this.shellService.sideMenuOptions.sidemenuActions =
        this.shellService.sideMenuOptions.sidemenuActions.filter(
          (action) => action.id != TIMESHEET_ACTIVITIES_ACTION_NAME,
        );
    }
  }

  public async onToolbarActionClicked(action: string): Promise<void> {
    switch (action) {
      case REPORT_ISSUE_ACTION_NAME:
        await this.reportIssue();
        break;

      case SEARCH_ACTION_NAME:
        await this.search();
        break;
    }
  }

  public async onSidemenuActionClicked(action: string): Promise<void> {
    switch (action) {
      case LOGOUT_ACTION_NAME:
        await this.logoutService.logout();
        break;
      case SETTINGS_ACTION_NAME:
        this.onSettingsPageClick();
        break;
      case INSPECTIONS_ACTION_NAME:
        this.onInspectionsPageClick();
        break;
      case DASHBOARDS_ACTION_NAME:
        this.onDashboardsPageClick();
        break;
      case RESOURCES_ACTION_NAME:
        this.onResourcesPageClick();
        break;
      case ABOUT_ACTION_NAME:
        this.onAboutPageClick();
        break;
      case MULTIOFFICE_ACTION_NAME:
        this.onMultiofficeIconClick();
        break;
      case BI_REPORTS_ACTION_NAME:
        this.onEmbeddedDashboardPageClick();
        break;
      case TIMESHEET_ACTIVITIES_ACTION_NAME:
        this.onTimesheetActivitiesPageClick();
        break;
    }
  }

  private setSideMenuStatuses(activeElementId: string) {
    const sideMenuOptions = document.querySelectorAll('*[id^="sidemenu-"]');
    sideMenuOptions?.forEach((el: HTMLElement) => {
      el?.classList.remove("selected-menu-option");
      el?.classList.add("menu-option");
    });

    setTimeout(() => {
      const el = document.getElementById(`sidemenu-${activeElementId}`);
      el?.classList.remove("menu-option");
      el?.classList.add("selected-menu-option");
    }, 0);
  }

  private onDashboardsPageClick(): void {
    this.router.navigate(["dashboards"]);
  }

  private onEmbeddedDashboardPageClick(): void {
    this.router.navigate(["dashboard"]);
  }

  private onTimesheetActivitiesPageClick(): void {
    this.router.navigate(["timesheet-activities"]);
  }

  private onResourcesPageClick(): void {
    this.featureFlagsService.isGeolocationFeatureEnabled()
      ? this.router.navigate(["resources/available-inspectors"])
      : this.router.navigate(["resources/app-version-control"]);
  }

  private onInspectionsPageClick(): void {
    this.router.navigate([""]);
  }

  private onSettingsPageClick(): void {
    this.router.navigate(["settings"]);
  }

  private onAboutPageClick(): void {
    this.router.navigate(["about"]);
  }

  private async onMultiofficeIconClick(): Promise<void> {
    const generateDocumentDialog = this.dialog.open(
      MultiofficeDialogComponent,
      {
        panelClass: "dialog",
        width: "60vw",
        height: "60vh",
      },
    );
    const result = await generateDocumentDialog?.afterClosed().toPromise();
    if (result) {
      await this.userService.putUserAccountId(result.accountId);
      this.user.selectedAcc = this.user.accountId;
      this.appShellService.refreshToolbarOptionsTitle();
      setTimeout(() => {
        globalThis.window.location.reload();
      }, 100);
    }
  }

  private async reportIssue(): Promise<void> {
    this.reportIssueService.openDialog();
  }

  private async search(): Promise<void> {
    const searchDialog = this.dialog.open(SearchDialogComponent, {
      panelClass: "dialog",
      autoFocus: false,
    });

    const result: SearchDialogResult = await searchDialog
      .afterClosed()
      .toPromise();
    if (result?.action) {
      this.inspectionListService.search(result.searchText, result.searchType);
    }
  }
}
